import React, { useMemo } from "react"

import { SlidersHorizontal } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ELIGIBILITY_OPTIONS, SHERIFF_TYPES } from "@/constants/compliance"
import { useComplianceFilters } from "@/contexts/ComplianceFiltersContext"
import { useComplianceFilterOptions } from "@/hooks/useComplianceFilterOptions"
import { useDepots } from "@/hooks/useDepots"

import { FilterSelect } from "./FilterSelect"

export interface ComplianceFiltersPopoverProps {
  title?: string
  description?: string
  showEligibilityFilter?: boolean
  onFilterChange?: () => void
}

/**
 * Shared compliance filters popover component
 * Used across ComplianceOverview, LicenceChecksTab, and ComplianceDataCompletenessTab
 */
export const ComplianceFiltersPopover: React.FC<ComplianceFiltersPopoverProps> = ({
  title = "Filter Compliance Overview",
  description = "Refine counts with the same filters as licence checks",
  showEligibilityFilter = false,
  onFilterChange,
}) => {
  const {
    locationFilter,
    setLocationFilter,
    sheriffTypeFilter,
    setSheriffTypeFilter,
    driverManagerTeamFilter,
    setDriverManagerTeamFilter,
    dspFilter,
    setDspFilter,
    eligibilityFilter,
    setEligibilityFilter,
    resetFilters,
  } = useComplianceFilters()

  const [filtersOpen, setFiltersOpen] = React.useState(false)
  const { data: depots = [] } = useDepots()
  const { driverManagerTeamOptions, dspOptions } = useComplianceFilterOptions()

  // Convert depots to filter options
  const locationOptions = useMemo(
    () => depots.map((depot) => ({ value: depot.name, label: depot.name })),
    [depots],
  )

  // Convert sheriff types to filter options
  const sheriffTypeOptions = useMemo(
    () => SHERIFF_TYPES.map((type) => ({ value: type, label: type })),
    [],
  )

  // Convert eligibility options to filter options
  const eligibilityOptions = useMemo(
    () => ELIGIBILITY_OPTIONS.map((option) => ({ value: option, label: option })),
    [],
  )

  const activeFilterCount = useMemo(() => {
    const filters = [
      locationFilter !== "all",
      sheriffTypeFilter !== "all",
      driverManagerTeamFilter !== "all",
      dspFilter !== "all",
      showEligibilityFilter && eligibilityFilter !== "all",
    ]
    return filters.filter(Boolean).length
  }, [
    locationFilter,
    sheriffTypeFilter,
    driverManagerTeamFilter,
    dspFilter,
    eligibilityFilter,
    showEligibilityFilter,
  ])

  const handleFilterChange = (setter: (value: string) => void) => {
    return (value: string) => {
      setter(value)
      onFilterChange?.()
    }
  }

  const handleResetFilters = () => {
    resetFilters()
    onFilterChange?.()
  }

  return (
    <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-10 gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge
              variant="secondary"
              className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0 text-xs"
            >
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              <h4 className="font-medium text-sm text-palette-jungleGreen900">{title}</h4>
              <p className="text-xs text-palette-jungleGreen500">{description}</p>
            </div>
            {activeFilterCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleResetFilters}
              >
                Reset
              </Button>
            )}
          </div>

          <div className="space-y-3">
            <FilterSelect
              id="location-filter"
              label="Location"
              value={locationFilter}
              onChange={handleFilterChange(setLocationFilter)}
              placeholder="All locations"
              allLabel="All Locations"
              options={locationOptions}
            />

            <FilterSelect
              id="sheriff-type-filter"
              label="Type"
              value={sheriffTypeFilter}
              onChange={handleFilterChange(setSheriffTypeFilter)}
              placeholder="All types"
              allLabel="All Types"
              options={sheriffTypeOptions}
            />

            <FilterSelect
              id="driver-manager-team-filter"
              label="Driver Manager Team"
              value={driverManagerTeamFilter}
              onChange={handleFilterChange(setDriverManagerTeamFilter)}
              placeholder="All teams"
              allLabel="All Teams"
              options={driverManagerTeamOptions}
            />

            <FilterSelect
              id="dsp-filter"
              label="DSP"
              value={dspFilter}
              onChange={handleFilterChange(setDspFilter)}
              placeholder="All DSPs"
              allLabel="All DSPs"
              options={dspOptions}
            />

            {showEligibilityFilter && (
              <FilterSelect
                id="eligibility-filter"
                label="Eligibility"
                value={eligibilityFilter}
                onChange={(value) => {
                  setEligibilityFilter(value as typeof eligibilityFilter)
                  onFilterChange?.()
                }}
                placeholder="All eligibility"
                allLabel="All"
                options={eligibilityOptions}
              />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
